<template>
    <card
        :bg="bg"
        class="o-card-fact"
        modifier="fact"
        :class="{ '-big' : big }"
    >
        <template #inner>
            <asset
                class="o-card-fact__bg"
                :size="big ? `w=${imageSize * 2}` : `w=${imageSize}`"
                :image="image"
            />
            <header class="o-card-fact__inner">
                <h3
                    v-if="title !== ''"
                    class="o-card-fact__title"
                >{{ title }}</h3>
                <span
                    v-if="rounded"
                    class="o-card-fact__rounded"
                    :class="{ '-circle' : rounded.length === 1 }"
                >
                    <span>{{ rounded }}</span>
                </span>
                <prismic-rich-text
                    class="o-card-fact__desc"
                    :field="desc"
                />
            </header>
        </template>

        <template v-if="behind" #behind>
            <span
                v-if="title !== ''"
                class="o-card-fact__title"
            >{{ title }}</span>
            <span
                v-if="rounded"
                class="o-card-fact__rounded"
                :class="{ '-circle' : rounded.length === 1 }"
            >
                <span>{{ rounded }}</span>
            </span>
            <prismic-rich-text
                class="o-card-fact__desc"
                :field="behind"
            />
        </template>
    </card>
</template>

<script>

import Asset from 'objects/Asset';
import PrismicRichText from 'objects/PrismicRichText';
import Card from 'components/Card';

import debounce from 'lodash/debounce';
import gsap from 'gsap';

export default {
    name: 'CardFact',
    components: {
        Asset,
        PrismicRichText,
        Card,
    },
    props: {
        big: {
            type: Boolean,
            default: false,
        },
        bg: {
            type: String,
        },
        title: {
            type: String,
        },
        rounded: {
            type: String,
        },
        desc: {
            type: Array,
        },
        image: {
            type: Object,
        },
        behind: {
            type: Array,
        }
    },
    data: () => ({
        imageSize: 320,
    }),
    computed: {

        hasImage() {
            return !!(this.image && this.image.url)
        },

        imageRatio() {

            if(!this.hasImage) {
                return 0;
            }

            return this.image.dimensions.width/this.image.dimensions.height
        }
    },
    mounted() {
        this.setSize()
        window.addEventListener('resize', this.setSize)
    },
    methods: {
        setSize() {

            let card = {
                w: this.$el.offsetWidth,
                h: this.$el.offsetHeight,
            };

            if(!this.hasImage) {
                return
            }

            let cardRatio = card.w/card.h

            if(cardRatio > this.imageRatio) {
                gsap.set(this.$refs.image, {
                    width: card.w,
                    height: card.w / this.imageRatio,
                })
            } else {
                gsap.set(this.$refs.image, {
                    width: card.h * this.imageRatio,
                    height: card.h,
                })
            }
        },
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.setSize)
    }
};
</script>

<style lang="scss">

.o-card-fact {

    &.-bright {

        .o-card-fact__rounded {
            background-color: $color-dark;
        }
    }

    &.-light {

        .o-card-fact__rounded {
            color: $color-light;
        }
    }

    .c-card__behind {

        .o-card-fact__title {
            opacity: 0;
        }

        .o-card-fact__rounded {
            color: var(--bg-behind);
        }
    }

    @media #{$breakpoint-horizontal} {

        &.-big {
            width: 40vw;
            max-width: vh(60);

            .c-card__inner {
                min-height: vh(90);
                max-height: vh(94);
            }

            .o-card-fact__inner,
            .c-card__behind {
                padding: 3.25em 3.25em 0;
            }

            .o-card-fact__title {
                margin-bottom: .2em;
                font-size: 3.125em;
            }

            .o-card-fact__rounded {
                height: 3em;

                &.-circle {
                    width: 3em;
                }
            }
        }
    }
}

.o-card-fact__bg {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    // overflow: hidden;

    // img {
    //     position: absolute;
    //     bottom: 0;
    //     left: 50%;
    //     display: block;
    //     width: auto;
    //     height: 100%;
    //     transform: translate(-50%, 0);
    // }

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: 50% 100%;
    }
}

.o-card-fact__inner {
    z-index: 1;
    padding: var(--padding-content) var(--padding-content) 80%;

    > *:nth-child(n+2) {
        margin-top: .5rem;
    }
}

.o-card-fact__title {
    display: block;
    font-family: ff("heading");
    font-size: 1.875em;
    font-weight: 400;
}

.o-card-fact__rounded {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2.5em;
    padding-right: 1em;
    padding-left: 1em;
    background-color: $color-bright;
    border-radius: 2em;

    &.-circle {
        width: 2.5em;
        padding: 0;
    }

    span {
        top: .09em;
        line-height: 1;
        font-family: ff("heavy");
        font-size: 1.3em;
    }
}

</style>
